import { useRef, useState } from "react";
import { useT, translateError } from "../../i18n/hooks.ts";
import { retryAemetProvenance, type AemetSeriesTrio } from "../../model/aemet/aemetRetry.ts";
import type { LatLon } from "../../model/geo.ts";
import type { AemetProvenance } from "../../model/storage.ts";
import { AemetAttributionNotice } from "./AemetAttributionNotice.tsx";
import { Dialog } from "../Dialog.tsx";
import { InlineProgress } from "../InlineProgress.tsx";
import { SourceCoverageDetail } from "../SourceCoverageDetail.tsx";

const VARIABLES = [
  { key: "precipitation", labelKey: "infoPopover.precipitation" },
  { key: "temperature", labelKey: "infoPopover.temperature" },
  { key: "etp", labelKey: "infoPopover.etp" },
] as const;

/**
 * The persisted, checkable-later detail behind a sub-basin's "Fetch from
 * AEMET" — replaces the old InfoIcon tooltip (too little room for the
 * concrete gap date ranges this shows now, and couldn't offer a retry
 * button at all). Once `aemetProvenance` exists, EditSubBasinDialog.tsx
 * shows a button that opens THIS instead of the original fetch button —
 * there's nothing more the fetch button would do here that "Retry N failed
 * requests" below doesn't already cover for the one thing that can still
 * be wrong (some windows failed) — see its own comment there.
 *
 * `centroid` is only needed for a retry (ETP's Hargreaves formula needs
 * latitude) — `null` disables the retry button with an explanation, same
 * as the original fetch button when the sub-basin has no linked geometry.
 */
export function AemetProvenanceDialog({
  provenance,
  columnName,
  centroid,
  series,
  onChangeSeries,
  onChangeProvenance,
  onClose,
}: {
  provenance: AemetProvenance;
  columnName: string;
  centroid: LatLon | null;
  series: AemetSeriesTrio;
  onChangeSeries: (series: AemetSeriesTrio) => void;
  onChangeProvenance: (provenance: AemetProvenance) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [retrying, setRetrying] = useState(false);
  const [retryDone, setRetryDone] = useState(0);
  const [retryTotal, setRetryTotal] = useState(0);
  const [retryError, setRetryError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  function stationName(code: string): string {
    return provenance.stationsUsed.find((s) => s.code === code)?.name ?? code;
  }

  async function retry() {
    if (!centroid) return;
    setRetrying(true);
    setRetryError(null);
    setRetryDone(0);
    setRetryTotal(provenance.failedWindows.length);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const result = await retryAemetProvenance(columnName, centroid, provenance, series, {
        onProgress: (done, total) => {
          setRetryDone(done);
          setRetryTotal(total);
        },
        signal: controller.signal,
      });
      onChangeSeries(result.series);
      onChangeProvenance(result.provenance);
    } catch (err) {
      if (!(err instanceof DOMException && err.name === "AbortError")) {
        setRetryError(translateError(err, t));
      }
    } finally {
      setRetrying(false);
      abortRef.current = null;
    }
  }

  return (
    <Dialog title={t("editSubBasinDialog.aemetReportTitle")} onClose={onClose}>
      <AemetAttributionNotice />

      <p>
        {provenance.method === "rocio"
          ? t(
              provenance.sourceUpdatedAt
                ? "editSubBasinDialog.aemetSourceRocio"
                : "editSubBasinDialog.aemetSourceRocioNoDate",
              {
                n: String(provenance.stationsUsed.length),
                aemetDate: provenance.sourceUpdatedAt ?? "",
                date: provenance.fetchedAt.slice(0, 10),
              }
            )
          : t("editSubBasinDialog.aemetSource", {
              name: provenance.stationsUsed[0]?.name ?? "",
              km: provenance.stationsUsed[0]?.distanceKm.toFixed(1) ?? "",
              date: provenance.fetchedAt.slice(0, 10),
            })}
      </p>
      <p className="muted-text">
        {t("aemetDialog.failedWindowRange", {
          from: provenance.dateRange.start,
          to: provenance.dateRange.end,
        })}
      </p>

      {VARIABLES.map(({ key, labelKey }) => (
        <div key={key}>
          <h4>{t(labelKey)}</h4>
          <SourceCoverageDetail bySource={provenance.sourceByDate[key]} stationName={stationName} />
        </div>
      ))}

      {provenance.failedWindows.length > 0 && (
        <div className="aemet-failed-windows">
          <p className="error">
            {t("aemetDialog.failedWindows", { n: String(provenance.failedWindows.length) })}
          </p>
          <ul className="zones-list">
            {provenance.failedWindows.map((w) => (
              <li key={`${w.start}_${w.end}`}>
                {t("aemetDialog.failedWindowRange", { from: w.start, to: w.end })}
              </li>
            ))}
          </ul>
          {retrying ? (
            <>
              <InlineProgress
                text={t("aemetDialog.retrying", {
                  done: String(retryDone),
                  total: String(retryTotal),
                })}
              />
              <button className="button" onClick={() => abortRef.current?.abort()}>
                {t("common.cancel")}
              </button>
            </>
          ) : (
            <button
              className="button"
              onClick={retry}
              disabled={!centroid}
              title={centroid ? undefined : t("editSubBasinDialog.aemetNoGeometry")}
            >
              {t("aemetDialog.retryFailedWindows", {
                n: String(provenance.failedWindows.length),
              })}
            </button>
          )}
          {retryError && <p className="error">{retryError}</p>}
        </div>
      )}

      <div className="dialog-actions">
        <button className="button" onClick={onClose}>
          {t("common.close")}
        </button>
      </div>
    </Dialog>
  );
}
